import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Play,
  Pause,
  Volume2,
  Headphones,
  Waves,
  Sparkles,
  Lock,
  ArrowRight,
} from 'lucide-react';

const TRACK_URL =
  'https://customer-assets-wrfwihn1.emergentagent.net/job_mastery-commerce/artifacts/6zymqah5_03%20Spiritual%20Guidance%2C%20Creativity%2C%20and%20Problem%20Solving.mp3';

const TRACK_TITLE = 'Spiritual Guidance, Creativity & Problem-Solving';

const formatTime = (t) => {
  if (!isFinite(t) || t < 0) return '0:00';
  const m = Math.floor(t / 60);
  const s = String(Math.floor(t % 60)).padStart(2, '0');
  return `${m}:${s}`;
};

export default function SampleTrackPreview() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrent(el.currentTime);
    const onMeta = () => setDuration(el.duration || 0);
    const onEnd = () => setPlaying(false);
    const onWait = () => setLoading(true);
    const onCanPlay = () => setLoading(false);
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('loadedmetadata', onMeta);
    el.addEventListener('ended', onEnd);
    el.addEventListener('waiting', onWait);
    el.addEventListener('canplay', onCanPlay);
    return () => {
      el.removeEventListener('timeupdate', onTime);
      el.removeEventListener('loadedmetadata', onMeta);
      el.removeEventListener('ended', onEnd);
      el.removeEventListener('waiting', onWait);
      el.removeEventListener('canplay', onCanPlay);
    };
  }, []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
      setPlaying(false);
    } else {
      setLoading(true);
      el.play()
        .then(() => {
          setPlaying(true);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          alert('Playback blocked by your browser. Please tap play again.');
        });
    }
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    el.currentTime = pct * duration;
    setCurrent(el.currentTime);
  };

  const pct = duration ? (current / duration) * 100 : 0;
  const remaining = duration ? duration - current : 0;

  return (
    <section className="relative py-16 md:py-20 border-t border-white/5 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-emerald-500/8 blur-3xl" />
        <div className="absolute right-1/3 top-1/3 w-[300px] h-[300px] rounded-full bg-amber-400/8 blur-3xl" />
      </div>

      <div className="relative max-w-3xl mx-auto px-5 md:px-6 text-center">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/5 text-emerald-200 text-[10px] md:text-xs font-semibold uppercase tracking-widest">
          <Headphones size={12} /> Free Preview &middot; Listen Before You Buy
        </div>
        <h2 className="mt-4 text-3xl md:text-5xl font-extrabold text-white tracking-tight">
          Hear a{' '}
          <span className="bg-gradient-to-r from-emerald-300 to-amber-200 bg-clip-text text-transparent">
            Real Track
          </span>{' '}
          From The Vault.
        </h2>
        <p className="mt-3 text-slate-400 max-w-2xl mx-auto text-sm md:text-base">
          This is one of the sacred tracks inside your bundle &mdash; recorded to
          gently guide the mind into a state of spiritual clarity, creative flow, and
          effortless problem-solving.
        </p>

        {/* Player card */}
        <div className="relative mt-8 md:mt-10 rounded-3xl border border-emerald-400/25 bg-gradient-to-b from-white/[0.05] to-transparent p-5 md:p-8 shadow-[0_0_60px_-25px_rgba(16,185,129,0.4)] overflow-hidden">
          <style>{`
            @keyframes stp-pulse-ring { 0% { transform: scale(1); opacity: 0.7; } 100% { transform: scale(1.9); opacity: 0; } }
            @keyframes stp-wave { 0%, 100% { transform: scaleY(0.28); } 50% { transform: scaleY(1); } }
            @keyframes stp-spin { to { transform: rotate(360deg); } }
          `}</style>

          {/* Badge above player */}
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-amber-400/30 bg-amber-500/10 text-amber-200 text-[10px] md:text-[11px] font-bold uppercase tracking-widest mb-5">
            <Sparkles size={11} /> Track 03 of 200+ in the Vault
          </div>

          <audio ref={audioRef} src={TRACK_URL} preload="metadata" />

          <div className="flex flex-col md:flex-row items-center gap-5 md:gap-6">
            {/* Play button with pulsing rings */}
            <div className="relative flex-shrink-0">
              {playing && (
                <>
                  <span
                    className="absolute inset-0 rounded-full border-2 border-emerald-400/60"
                    style={{ animation: 'stp-pulse-ring 2s ease-out infinite' }}
                  />
                  <span
                    className="absolute inset-0 rounded-full border-2 border-amber-400/50"
                    style={{ animation: 'stp-pulse-ring 2s ease-out infinite 0.6s' }}
                  />
                </>
              )}
              <button
                onClick={toggle}
                aria-label={playing ? 'Pause preview' : 'Play preview'}
                className={`relative w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-transform hover:scale-105 ${
                  playing
                    ? 'bg-gradient-to-br from-emerald-400 to-emerald-600 shadow-[0_0_50px_-5px_rgba(16,185,129,0.9)]'
                    : 'bg-gradient-to-br from-emerald-300 to-emerald-500 shadow-[0_0_40px_-5px_rgba(16,185,129,0.7)]'
                }`}
              >
                {loading ? (
                  <span
                    className="w-7 h-7 border-[3px] border-slate-900/30 border-t-slate-900 rounded-full"
                    style={{ animation: 'stp-spin 0.9s linear infinite' }}
                  />
                ) : playing ? (
                  <Pause size={30} className="text-slate-900" strokeWidth={2.5} />
                ) : (
                  <Play
                    size={32}
                    className="text-slate-900 ml-1"
                    strokeWidth={2.5}
                    fill="currentColor"
                  />
                )}
              </button>
            </div>

            {/* Info + progress */}
            <div className="flex-1 w-full text-left">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-white font-bold text-base md:text-lg leading-tight truncate">
                    {TRACK_TITLE}
                  </div>
                  <div className="text-slate-400 text-[11px] md:text-sm flex items-center gap-2 mt-0.5">
                    <Waves size={12} className="text-emerald-300 flex-shrink-0" />
                    Guided frequency session &middot; Best with headphones
                  </div>
                </div>
                <div className="text-right hidden sm:block flex-shrink-0">
                  <div className="text-emerald-300 font-mono text-sm tabular-nums">
                    {formatTime(current)}
                  </div>
                  <div className="text-slate-500 text-[10px] uppercase tracking-widest">
                    Elapsed
                  </div>
                </div>
              </div>

              {/* Progress bar (click-to-seek) */}
              <div
                onClick={seek}
                className="mt-4 h-2 rounded-full bg-white/10 overflow-hidden cursor-pointer relative"
              >
                <div
                  className="h-full bg-gradient-to-r from-emerald-400 to-amber-300 transition-[width] duration-150"
                  style={{ width: `${pct}%` }}
                />
                <div
                  className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-white shadow-md pointer-events-none transition-[left] duration-150"
                  style={{ left: `calc(${pct}% - 6px)` }}
                />
              </div>

              <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500 font-mono tabular-nums">
                <span>{formatTime(current)}</span>
                <span>-{formatTime(remaining)}</span>
              </div>

              {/* Waveform bars while playing */}
              {playing && (
                <div className="mt-3 flex items-end gap-1 h-6">
                  {Array.from({ length: 34 }).map((_, i) => (
                    <span
                      key={i}
                      className="flex-1 bg-gradient-to-t from-emerald-500 to-amber-300 rounded-sm origin-bottom"
                      style={{
                        animation: `stp-wave ${0.9 + (i % 5) * 0.15}s ease-in-out infinite`,
                        animationDelay: `${i * 0.05}s`,
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[11px] md:text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5">
              <Volume2 size={12} className="text-emerald-300" /> Turn volume to a comfortable level
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Headphones size={12} className="text-emerald-300" /> Headphones strongly recommended
            </span>
          </div>
        </div>

        {/* Vault teaser CTA */}
        <div className="relative mt-8 md:mt-10 rounded-2xl border border-amber-400/25 bg-gradient-to-r from-amber-400/[0.06] via-emerald-400/[0.04] to-transparent p-5 md:p-6">
          <div className="flex flex-col md:flex-row items-center gap-4 text-left">
            <div className="w-12 h-12 flex-shrink-0 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-[0_10px_30px_-10px_rgba(251,191,36,0.6)]">
              <Lock className="text-slate-900" size={22} />
            </div>
            <div className="flex-1 text-center md:text-left">
              <div className="text-white font-extrabold text-base md:text-lg leading-tight">
                This is just 1 gem. A vault of hundreds more is waiting inside.
              </div>
              <div className="mt-1 text-slate-300 text-xs md:text-sm">
                200+ guided sessions across guidance, creativity, sleep, focus, healing
                and manifestation &mdash; unlocked the moment you claim your bundle.
              </div>
            </div>
            <Link
              to="/checkout"
              className="w-full md:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold text-sm shadow-[0_10px_30px_-8px_rgba(16,185,129,0.55)] transition-colors whitespace-nowrap"
            >
              Unlock The Full Vault
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
